import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../../components/Button/Button';
import { useUnprotectedLayout } from '../../components/UnprotectedRouteLayout/UnprotectedRouteLayout';

const SSOCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { setBackgroundImagePath, setSideImagePath } = useUnprotectedLayout();

  useEffect(() => {
    setSideImagePath!('/images/enbridge-logo-in-building.jpg');
    setBackgroundImagePath!('/images/earth.jpg');

    const handleCallback = async () => {
      try {
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (errorParam) {
          setError(errorDescription || 'Authentication failed');
          setLoading(false);
          return;
        }

        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code) {
          setError('No authorization code received');
          setLoading(false);
          return;
        }

        const savedState = sessionStorage.getItem('oauth_state');
        if (state !== savedState) {
          setError('Invalid state parameter - possible CSRF attack');
          setLoading(false);
          return;
        }

        const apiEndpoint = process.env.REACT_APP_APPSYNC_GRAPHQL_ENDPOINT?.replace('/graphql', '') || '';
        
        const response = await fetch(`${apiEndpoint}/auth/federated/exchange`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_APPSYNC_API_KEY || '',
          },
          body: JSON.stringify({
            code,
            redirectUri: `${window.location.origin}/sso/callback`,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Token exchange failed');
        }

        const data = await response.json();

        sessionStorage.removeItem('oauth_state');

        navigate('/', { replace: true });

      } catch (err: any) {
        console.error('SSO callback error:', err);
        setError(err.message || 'Something went wrong');
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setBackgroundImagePath, setSideImagePath]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '100px' }}>
        <h2>Completing sign in...</h2>
        <p>Please wait while we verify your credentials.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', marginTop: '100px' }}>
        <h2>Authentication Error</h2>
        <p style={{ color: 'red' }}>{error}</p>
        <Button onClick={() => navigate('/auth/login')}>
          Return to Login
        </Button>
      </div>
    );
  }

  return null;
};

export default SSOCallback;